import axios from "axios";
import i18next from "i18next";
import { cachedRead, itemsOf, mutate, readCache } from "../../../offline";
import { applyClockActions } from "../../../offline/clockOverlay";

const base = () => `${process.env.REACT_APP_BACKEND_URL}/attendance`;
const auth = () => ({ headers: { Authorization: "Bearer " + localStorage.getItem("token") } });

// The employee's wall clock decides which calendar day a time belongs to
export const TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin";
  } catch {
    return "Europe/Berlin";
  }
})();

const data = promise => promise.then(res => res.data);

// Clock actions made offline wait in the outbox (src/offline); until they are synced the clock
// card shows the last known day with them applied.
const withQueuedClocks = async (userId, answer) => {
  const queued = (await itemsOf("attendance", userId)).filter(item => item.kind === "clock");
  if (!queued.length) return answer;
  return {
    ...answer,
    data: applyClockActions(
      answer?.data,
      queued.map(item => item.body),
      { timeZone: TZ }
    )
  };
};

const todayKey = userId => `attendance:today:${userId}`;

export const fetchToday = userId =>
  data(
    cachedRead(todayKey(userId), () =>
      axios.get(`${base()}/today/${userId}`, { ...auth(), params: { tz: TZ } })
    )
  ).then(answer => withQueuedClocks(userId, answer));

export const fetchDay = (userId, date) =>
  data(
    cachedRead(`attendance:day:${userId}:${date}`, () =>
      axios.get(`${base()}/day/${userId}`, { ...auth(), params: { date, tz: TZ } })
    )
  );

export const saveDay = (userId, body) =>
  data(axios.put(`${base()}/day/${userId}`, { ...body, tz: TZ }, auth()));

const CLOCK_LABELS = {
  WORK: "Start work",
  TRAVEL: "Start travel",
  BREAK: "Break",
  STOP: "End of day"
};

/**
 * action: WORK | TRAVEL | BREAK | STOP; at: Date (omit for now). The tap time is always sent,
 * so an action queued offline is recorded when it happened, not when it is synced. Only when
 * "now" is refused as being in the future (device clock ahead of the server) it is sent again
 * without a time.
 */
export const clock = async (userId, { action, at, project, paid, note }) => {
  const tapped = at || new Date();
  const body = { action, at: tapped.toISOString(), project, paid, note, tz: TZ };
  const send = payload =>
    mutate({
      module: "attendance",
      kind: "clock",
      method: "POST",
      url: `${base()}/clock/{id}`,
      target: userId,
      body: payload,
      label: `${i18next.t(CLOCK_LABELS[action] || action, { ns: "dashboard" })} ${tapped
        .toTimeString()
        .slice(0, 5)}`
    });
  let result;
  try {
    result = await send(body);
  } catch (error) {
    const inFuture = (error.response?.data?.errors || []).some(e => e.code === "IN_FUTURE");
    if (at || error.response?.status !== 400 || !inFuture) throw error;
    result = await send({ ...body, at: undefined });
  }
  if (!result.queued) return result.response.data;
  const known = (await readCache(todayKey(userId)))?.data;
  return { success: true, queued: true, ...(await withQueuedClocks(userId, known || {})) };
};

export const fetchStatistics = userId =>
  data(
    cachedRead(`attendance:statistics:${userId}`, () =>
      axios.get(`${base()}/statistics/${userId}`, { ...auth(), params: { tz: TZ } })
    )
  );

export const fetchRange = (userId, startDate, endDate) =>
  data(
    cachedRead(`attendance:range:${userId}:${startDate.toISOString()}`, () =>
      axios.get(`${base()}/range/${userId}`, {
        ...auth(),
        params: { startDate: startDate.toISOString(), endDate: endDate.toISOString() }
      })
    )
  );

/**
 * Arbeitszeitnachweis file (Blob) for a period; format: pdf | xlsx | csv.
 * userId "team" = all employees of the company in one file.
 */
export const fetchExport = (userId, { from, to, format, lng, employees }) =>
  axios
    .get(userId === "team" ? `${base()}/export-team` : `${base()}/export/${userId}`, {
      ...auth(),
      params: {
        from,
        to,
        format,
        lng,
        tz: TZ,
        employees: employees?.length ? employees.join(",") : undefined
      },
      responseType: "blob"
    })
    .then(res => res.data)
    .catch(async error => {
      // Error bodies arrive as a Blob too; surface the server's message
      const blob = error.response?.data;
      if (blob instanceof Blob) {
        try {
          error.response.data = JSON.parse(await blob.text());
        } catch {
          // not JSON: keep the original error
        }
      }
      throw error;
    });

/** People a team export of the period would cover: [{ _id, name, personnelNo, owner, deleted, hasTimes }] */
export const fetchTeamMembers = (from, to) =>
  data(axios.get(`${base()}/team-members`, { ...auth(), params: { from, to, tz: TZ } }));

// Projects the user may book time on (server-scoped)
export const fetchProjects = () =>
  cachedRead("projects:limited", () =>
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/projects/limited`, { pageSize: 200 }, auth())
  ).then(res => (Array.isArray(res.data?.data) ? res.data.data : []));
